import { v4 as uuidv4 } from "uuid";
import { ROLE_DISPLAY_NAMES } from "./constants";
import {
  getContactDisplayName,
  getContactDisplayTitle,
  normalizeApiContact,
} from "./requestContactDesignations";

/** Extract email domain (lowercase) or null. */
export function extractEmailDomain(email) {
  if (!email || typeof email !== "string") return null;
  const trimmed = email.trim();
  const at = trimmed.lastIndexOf("@");
  if (at < 0 || at >= trimmed.length - 1) return null;
  return trimmed.slice(at + 1).toLowerCase();
}

/**
 * Domain must match a domain already used by other users of the company.
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export function validateEmailDomainAgainstOrgUsers(email, orgUserEmails) {
  const domain = extractEmailDomain(email);
  if (!domain) {
    return { ok: false, message: "A valid email address is required." };
  }

  const knownDomains = new Set(
    (orgUserEmails || []).map(extractEmailDomain).filter(Boolean),
  );

  if (knownDomains.size === 0) {
    return { ok: true };
  }

  if (!knownDomains.has(domain)) {
    const list = [...knownDomains].sort().join(", ");
    return {
      ok: false,
      message: `Email domain must match an existing domain for this organization (${list}).`,
    };
  }

  return { ok: true };
}

export function formatPlatformRoleLabel(roleKey) {
  if (!roleKey) return null;
  const key = String(roleKey).trim();
  return ROLE_DISPLAY_NAMES[key] || key;
}

export function resolveDisplayedPlatformRole(contact, orgContact, assignedPlatformRole) {
  const fromOrg = orgContact?.userRole || null;
  const explicit = contact?.userRole || null;
  const isAdHoc = Boolean(contact?.isAdHoc || contact?.is_ad_hoc);
  const assigned = isAdHoc && assignedPlatformRole ? assignedPlatformRole : null;
  return formatPlatformRoleLabel(fromOrg || explicit || assigned);
}

export function overviewOrgUsersQueryKey(mode, orgId) {
  return mode === "sponsor"
    ? ["getAllSponsorUserList", orgId]
    : ["providerUsersByProviderId", orgId];
}

export async function refreshOverviewOrgUsers(queryClient, mode, orgId) {
  if (!queryClient || orgId == null || orgId === "") return;
  const queryKey = overviewOrgUsersQueryKey(mode, orgId);
  await queryClient.invalidateQueries({ queryKey });
  await queryClient.refetchQueries({ queryKey, type: "active" });
}

/** Sponsor-side sub-role payload from Identity / Sponsor list APIs. */
export function resolveSponsorSubRoleSource(user) {
  if (!user) return [];
  return (
    user.sponsor_subrole_dto ||
    user.sponsor_sub_roles ||
    user.sub_roles ||
    user.subRoles ||
    user.sub_role ||
    []
  );
}

export function getUserSubRoleNames(user) {
  if (!user) return [];
  const raw =
    user.vendor_subrole_dto ||
    user.sponsor_subrole_dto ||
    user.provider_user_subrole ||
    user.sub_roles ||
    user.subRoles ||
    [];

  if (!Array.isArray(raw)) {
    return String(raw)
      .split("/")
      .map((s) => s.trim())
      .filter(Boolean);
  }

  return raw
    .map((r) => {
      if (typeof r === "string") return r.trim();
      return String(
        r?.sub_role ?? r?.Subrole ?? r?.subrole ?? r?.role ?? r?.name ?? "",
      ).trim();
    })
    .filter(Boolean);
}

export function contactSignature(list) {
  return (list || [])
    .map((c) => String(c?.email || "").toLowerCase())
    .filter(Boolean)
    .sort()
    .join("|");
}

export function normalizeOverviewContact(contact, index = 0) {
  if (!contact) return null;
  const normalized = normalizeApiContact(contact, index);
  const email = String(normalized.email || "").trim().toLowerCase();
  if (!email) return null;

  const subRoles = getUserSubRoleNames(normalized);
  const explicitPlatformRole =
    contact.userRole ??
    contact.user_role ??
    contact.platformRole ??
    contact.platform_role ??
    null;
  const primaryRole = subRoles[0] || explicitPlatformRole || null;

  return {
    ...normalized,
    email,
    id: email,
    name: getContactDisplayName(normalized) || email,
    role: getContactDisplayTitle(normalized) || "Team Member",
    job_title: getContactDisplayTitle(normalized) || normalized.job_title || "",
    userRole: formatPlatformRoleLabel(primaryRole),
    isPrimary: Boolean(
      normalized.isPrimary ??
        normalized.is_primary ??
        normalized.rfi_is_primary ??
        normalized.assessment_is_primary ??
        normalized.is_request_primary ??
        normalized.is_vendor_category_primary,
    ),
    isRequestContact: Boolean(
      normalized.isRequestContact ??
        normalized.rfi_request_contact_status ??
        normalized.assessment_request_contact_status ??
        normalized.is_request_contact,
    ),
    isAdHoc: Boolean(normalized.isAdHoc ?? normalized.is_ad_hoc),
  };
}

export function toRequestContactPayload(contacts) {
  return (contacts || []).map((c, index) => ({
    email: String(c.email || "").trim().toLowerCase(),
    first_name: c.first_name ?? c.firstName ?? "",
    last_name: c.last_name ?? c.lastName ?? "",
    job_title: c.job_title ?? c.title ?? c.role ?? "",
    is_request_primary: Boolean(c.is_request_primary ?? c.isPrimary ?? index === 0),
    is_ad_hoc: Boolean(c.isAdHoc ?? c.is_ad_hoc),
    sort_order: c.sort_order ?? index,
    is_vendor_category_primary: Boolean(
      c.is_vendor_category_primary ?? c.isPrimary,
    ),
  }));
}

export function formatOverviewReadOnlyContact(contact) {
  const name =
    String(contact?.name || "").trim() ||
    [contact?.first_name, contact?.last_name].filter(Boolean).join(" ").trim();
  const email = String(contact?.email ?? "").trim();
  if (name && email) return `${name} | ${email}`;
  return name || email || "";
}

export function pluralizeContactLabel(base, count) {
  return count > 1 ? `${base}s` : base;
}

/**
 * Resolve saved contacts from API list or legacy single email field.
 * @param {object} details
 * @param {{ listKeys: string[], legacyEmailKeys: string[] }} options
 */
export function resolveSavedContactsFromDetails(
  details,
  { listKeys = [], legacyEmailKeys = [] } = {},
) {
  for (const key of listKeys) {
    const fromApi = details?.[key];
    if (Array.isArray(fromApi) && fromApi.length > 0) {
      return fromApi.map((c, i) => normalizeOverviewContact(c, i)).filter(Boolean);
    }
  }
  for (const key of legacyEmailKeys) {
    const email = details?.[key];
    if (email) {
      return [normalizeOverviewContact({ email }, 0)].filter(Boolean);
    }
  }
  return [];
}

/** Positive HyperComply organization id, or null when missing/invalid. */
export function coerceHyperComplyOrgId(...candidates) {
  for (const value of candidates) {
    if (value == null || value === "") continue;
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) continue;
    return Math.trunc(n);
  }
  return null;
}

function flattenValidationMessages(errors) {
  if (!errors || typeof errors !== "object") return [];
  return Object.values(errors)
    .flat()
    .filter((entry) => typeof entry === "string" && entry.trim())
    .map((entry) => entry.trim());
}

/** Prefer API message over generic axios status text. */
export function resolveApiErrorMessage(err, fallback = "Something went wrong.") {
  const data = err?.response?.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  if (data?.message) return data.message;
  if (data?.Message) return data.Message;
  const validation = flattenValidationMessages(data?.errors);
  if (validation.length) return validation.join(" ");
  if (typeof data?.title === "string" && data.title.trim()) return data.title.trim();
  if (typeof data?.detail === "string" && data.detail.trim()) return data.detail.trim();
  if (err?.message && !/^Request failed with status code \d+$/i.test(err.message)) {
    return err.message;
  }
  return fallback;
}

/** Manager role + request permission when creating sponsor users from Overview. */
export const OVERVIEW_SPONSOR_MANAGER_PROFILE = {
  vqq: { managerSubRole: "RFI Manager", requestRight: "RFI Requests" },
  vqa: { managerSubRole: "VQA Manager", requestRight: "VQA Requests" },
};

export function resolveOverviewSponsorName(details, sponsorId, sponsorDetails, kind) {
  if (kind === "vqq") {
    return (
      details?.sponsorName ||
      details?.SponsorName ||
      sponsorDetails?.sponsor_name ||
      ""
    );
  }

  return (
    details?.sponsor_name ||
    details?.vqa_request_sponsors?.find(
      (s) => String(s.sponsor_id) === String(sponsorId),
    )?.sponsor_name ||
    sponsorDetails?.sponsor_name ||
    ""
  );
}

export async function createOverviewSponsorUser(
  addSponsorUser,
  { form, sponsorId, sponsorName, hypercomplyOrgId, profile },
) {
  await addSponsorUser(
    buildOverviewSponsorUserCreatePayload({
      form,
      sponsorId,
      sponsorName,
      hypercomplyOrgId,
      managerSubRole: profile.managerSubRole,
      requestRight: profile.requestRight,
    }),
  );
}

/**
 * Payload for POST /Identity/SponsorUsers from Overview "Create User".
 * Requires sponsor HyperComply org id (Identity returns 400 without it).
 */
export function buildOverviewSponsorUserCreatePayload({
  form,
  sponsorId,
  sponsorName,
  hypercomplyOrgId,
  managerSubRole,
  requestRight,
}) {
  const orgId = Number(hypercomplyOrgId);
  if (!Number.isFinite(orgId) || orgId <= 0) {
    throw new Error(
      "Could not resolve organization for this sponsor. Please try again in a moment.",
    );
  }

  return {
    ...form,
    sponsor_id: sponsorId,
    sponsor_name: sponsorName || "",
    is_active: true,
    contact_preference: "Mobile_Phone",
    hypercomply_org_id: orgId,
    sponsor_subrole_dto: [
      { sponsor_subrole_id: uuidv4(), sub_role: managerSubRole },
    ],
    sponsor_rights_dto: [{ sponsor_rights_id: uuidv4(), rights: requestRight }],
  };
}
